// Helpers for resolving which queuedSetup of a pending meal applies to a dining area.
//
// Rules:
//  - A setup explicitly tied to a diningAreaId only applies to that dining area.
//  - A setup with a null diningAreaId is the facility-wide fallback, but it only
//    applies to dining areas that are never explicitly scheduled anywhere in the
//    pending meals (those areas run their own menu schedule).

export const getScheduledDiningAreaIds = (pendingMeals) => {
  const ids = new Set();
  pendingMeals?.forEach((meal) =>
    meal?.queuedSetups?.forEach((setup) => {
      if (setup?.diningAreaId != null) {
        ids.add(setup.diningAreaId);
      }
    })
  );
  return ids;
};

export const findMealBySetupId = (pendingMeals, setupId) =>
  pendingMeals?.find((meal) => meal?.queuedSetups?.some((s) => s?.id === setupId)) ?? null;

export const getResidentDiningAreaId = (resident, mealName) => {
  const mealType = mealName?.toLowerCase();
  return mealType ? resident?.[`${mealType}DiningAreaId`] ?? null : null;
};

export const resolveSetupForDiningArea = (pendingMeals, meal, diningAreaId, setups) => {
  const queuedSetups = setups?.length ? setups : meal?.queuedSetups ?? [];
  const exact = queuedSetups.find((s) => s?.diningAreaId === diningAreaId);
  if (exact) {
    return exact;
  }
  // a specific meal's setups were pinned by the caller (e.g. backfilling an old/queued meal) — trust
  // that meal's own null fallback instead of the cross-meal heuristic, which can false-positive when
  // pendingMeals spans many days (order management includes past meals) and the area is explicit elsewhere
  if (!setups?.length && diningAreaId != null && getScheduledDiningAreaIds(pendingMeals).has(diningAreaId)) {
    return null;
  }
  return queuedSetups.find((s) => s?.diningAreaId == null) ?? null;
};


export const findActiveMealForDiningArea = (pendingMeals, diningAreaId) => {
  for (const meal of pendingMeals ?? []) {
    const setup = resolveSetupForDiningArea(pendingMeals, meal, diningAreaId);
    if (setup?.isActive) {
      return { meal, setup };
    }
  }
  return null;
};

export const getMealSetupStatus = (pendingMeals, meal, diningAreaId, diningAreaName, setups, allowInactive = false) => {
  const setup = resolveSetupForDiningArea(pendingMeals, meal, diningAreaId, setups);
  const mealLabel = meal?.mealName ?? "This meal";
  const areaSubject = diningAreaName || "This dining area";
  const areaObject = diningAreaName || "this dining area";

  if (setup?.isActive || (allowInactive && setup)) {
    return { setup, isValid: true, message: "", activeMeal: meal, activeSetup: setup };
  }

  const active = findActiveMealForDiningArea(pendingMeals, diningAreaId);
  const reason = setup
    ? `${mealLabel} is no longer active for ${areaObject}.`
    : `${mealLabel} is not scheduled for ${areaObject}.`;
  const suggestion = active
    ? `This dining area is currently serving ${active.meal.mealName}.`
    : `${areaSubject} has no active meal right now.`;

  return {
    setup,
    isValid: false,
    message: `${reason} ${suggestion}`,
    activeMeal: active?.meal ?? null,
    activeSetup: active?.setup ?? null,
  };
};
